/*
  Siraat chat launcher and welcome panel for Gorgias Chat.
  Markup: snippets/gorgias-chat-launcher.liquid. Styles: assets/gorgias-chat-launcher.css.

  0. Stays off until switched on: by the A/B test variant (window.SiraatChat.enable() or window.siraatChatEnabled),
     by a preview link with ?siraat_chat=on, or by chat_always_on in the snippet. While off it does nothing at all.
  1. Loads the Gorgias install script after the page has loaded, unless Gorgias is already on the page.
  2. Waits for the widget with the init wrapper from the Gorgias docs, then hides the default Gorgias button.
  3. The round button opens the Siraat welcome panel with a message from Noah and quick questions. A quick question
     opens the Gorgias chat and sends that question; "Ask something else" opens the chat empty.
     With unread messages the button opens the chat directly. Requests made before Gorgias is ready are queued.
  4. Lifts the button and panel above sticky add-to-cart bars and cookie banners in the bottom-right corner.

  Gorgias API used (all from the Gorgias docs on chat customization):
  GorgiasChat.init(), open(), close(), isOpen(), setPage(), sendMessage(), setCurrentInput(),
  on('widget:opened' | 'widget:closed' | 'unreadCount' | 'message:sent').
*/
(function () {
  'use strict';

  var wrapper = document.querySelector('.siraat-chat');
  if (!wrapper || window.__siraatChatLauncher) return;
  window.__siraatChatLauncher = true;

  /* ---------- 0. On/off switch for the A/B test ---------- */

  var started = false;
  window.SiraatChat = { enable: start };

  // ?siraat_chat=on or off in the URL, remembered for the visit so a preview survives clicking around
  function previewSwitchedOn() {
    var match = /[?&]siraat_chat=(on|off)(&|$)/.exec(window.location.search);
    try {
      if (match) window.sessionStorage.setItem('siraat_chat', match[1]);
      return window.sessionStorage.getItem('siraat_chat') === 'on';
    } catch (error) {
      return Boolean(match) && match[1] === 'on';
    }
  }

  if (wrapper.getAttribute('data-enabled') === 'true' || window.siraatChatEnabled === true || previewSwitchedOn()) {
    start();
  }

  function start() {
    if (started) return;
    started = true;
    wrapper.classList.add('is-enabled');

    var root = document.documentElement;
    var button = wrapper.querySelector('.siraat-chat-launcher');
    var panel = wrapper.querySelector('.siraat-chat-panel');
    var startButton = wrapper.querySelector('.siraat-chat-panel__start');
    var badge = wrapper.querySelector('.siraat-chat-launcher__badge');
    var GORGIAS_HOST = 'https://config.gorgias.chat/';
    var GIVE_UP_AFTER_MS = 20000; // counted from window load
    var CONFIRM_SEND_MS = 4000; // time Gorgias gets to confirm a quick question was sent
    var ready = false;
    var chatQueued = false;
    var queuedQuestion = '';
    var unconfirmedQuestion = '';
    var loadRequested = false;
    var chatOpen = false;
    var unread = 0;
    var greeted = false;

    // While this class is on <html>, the CSS hides the default Gorgias button
    root.classList.add('siraat-chat-launcher-active');

    /* ---------- 1. Load Gorgias without blocking the page ---------- */

    function gorgiasAlreadyOnPage() {
      return Boolean(
        window.GorgiasChat ||
          document.getElementById('gorgias-chat-container') ||
          document.querySelector('script[src*="config.gorgias.chat"]')
      );
    }

    function loadGorgias() {
      if (loadRequested) return;
      loadRequested = true;

      // Still the placeholder (or not a Gorgias URL): rely on the Gorgias install that is already on the page
      var src = wrapper.getAttribute('data-gorgias-src') || '';
      if (src.indexOf(GORGIAS_HOST) !== 0 || gorgiasAlreadyOnPage()) return;

      var script = document.createElement('script');
      script.id = src.indexOf('/bundle-loader/') !== -1 ? 'gorgias-chat-widget-install-v3' : 'gorgias-chat-widget-install-v2';
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }

    function onPageLoaded(callback) {
      if (document.readyState === 'complete') {
        callback();
      } else {
        window.addEventListener('load', callback, { once: true });
      }
    }

    function whenIdle(callback) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 3000 });
      } else {
        setTimeout(callback, 200);
      }
    }

    onPageLoaded(function () {
      whenIdle(loadGorgias);

      // Gorgias blocked or failed: hide our button and panel instead of showing ones that never work
      setTimeout(function () {
        if (ready) return;
        closePanel(false);
        wrapper.setAttribute('data-state', 'unavailable');
        root.classList.remove('siraat-chat-launcher-active');
      }, GIVE_UP_AFTER_MS);
    });

    /* ---------- 2. Wait for Gorgias (init wrapper from the Gorgias docs) ---------- */

    var initGorgiasChatPromise = window.GorgiasChat
      ? window.GorgiasChat.init()
      : new Promise(function (resolve) {
          window.addEventListener('gorgias-widget-loaded', function () {
            resolve();
          });
        });

    // Documented "Replace the chat widget button" approach: wait until the button iframe has loaded, then hide it
    function hideDefaultButton() {
      var attempts = 0;
      var timer = setInterval(function () {
        attempts += 1;
        var iframe = document.querySelector('#gorgias-chat-container #chat-button');
        var head = null;
        try {
          head = iframe && iframe.contentWindow && iframe.contentWindow.document.querySelector('head');
        } catch (error) {
          head = null;
        }

        if (!head || !head.children.length) {
          if (attempts > 200) clearInterval(timer); // stop after about 10 seconds, the CSS rule still hides it
          return;
        }

        clearInterval(timer);
        iframe.style.display = 'none';
      }, 50);
    }

    function updateBadge(count) {
      unread = Number(count) || 0;
      if (unread > 0) {
        badge.textContent = unread > 9 ? '9+' : String(unread);
        badge.hidden = false;
      } else {
        badge.textContent = '';
        badge.hidden = true;
      }
    }

    initGorgiasChatPromise.then(function () {
      hideDefaultButton();

      GorgiasChat.on('widget:opened', function () {
        chatOpen = true;
        closePanel(false);
        syncExpanded();
      });

      GorgiasChat.on('widget:closed', function () {
        chatOpen = false;
        syncExpanded();
        hideDefaultButton(); // keep the default button hidden after the chat closes
      });

      GorgiasChat.on('unreadCount', updateBadge);

      GorgiasChat.on('message:sent', function () {
        unconfirmedQuestion = ''; // the quick question went out
      });

      ready = true;
      chatOpen = GorgiasChat.isOpen();
      wrapper.removeAttribute('data-state');
      button.removeAttribute('aria-busy');
      root.classList.add('siraat-chat-launcher-active');
      syncExpanded();

      if (chatQueued) {
        chatQueued = false;
        button.classList.remove('is-queued');
        startButton.removeAttribute('aria-busy');
        openChat(queuedQuestion);
        queuedQuestion = '';
      }
    });

    /* ---------- 3. Panel and chat ---------- */

    function isPanelOpen() {
      return !panel.hidden;
    }

    function syncExpanded() {
      button.setAttribute('aria-expanded', isPanelOpen() || chatOpen ? 'true' : 'false');
    }

    function focusFirstQuestion() {
      if (isPanelOpen()) (panel.querySelector('.siraat-chat-panel__chip') || startButton).focus({ preventScroll: true });
    }

    function openPanel() {
      panel.hidden = false;
      syncExpanded();

      // First open on this page: show Noah "typing" for a moment before his message and the questions appear.
      // The questions are invisible while he types, so focus moves to the first one afterwards.
      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!greeted && !reduceMotion) {
        greeted = true;
        panel.classList.add('is-typing');
        startButton.focus({ preventScroll: true });
        setTimeout(function () {
          panel.classList.remove('is-typing');
          focusFirstQuestion();
        }, 900);
        return;
      }

      greeted = true;
      focusFirstQuestion();
    }

    function closePanel(returnFocus) {
      if (!isPanelOpen()) return;
      panel.hidden = true;
      syncExpanded();
      if (returnFocus) button.focus({ preventScroll: true });
    }

    // Opens the Gorgias chat. With a question, the chat is switched to its chat view first: the AI Agent home screen
    // ("Ask anything") holds a sent message back until the shopper goes to the chat, so the question would never
    // arrive. Then open() and sendMessage() as in the Gorgias docs, so Noah answers straight away.
    function openChat(question) {
      if (ready) {
        closePanel(false);
        if (question) showChatView();
        GorgiasChat.open();
        if (question) {
          showChatView(); // again once open, in case the widget resets its view on opening
          sendQuestion(question);
        }
        return;
      }

      // Gorgias still loading: remember the request and start loading now if we had not yet
      chatQueued = true;
      queuedQuestion = question || '';
      button.classList.add('is-queued');
      startButton.setAttribute('aria-busy', 'true');
      loadGorgias();
    }

    function showChatView() {
      if (typeof GorgiasChat.setPage === 'function') GorgiasChat.setPage('chat');
    }

    // Sends the question as the shopper. If Gorgias has not confirmed it (message:sent) after a few seconds, the
    // question is put in the chat input instead, so the shopper only has to press send.
    function sendQuestion(question) {
      unconfirmedQuestion = question;
      GorgiasChat.sendMessage(question);
      setTimeout(function () {
        if (unconfirmedQuestion !== question) return;
        unconfirmedQuestion = '';
        if (typeof GorgiasChat.setCurrentInput === 'function') GorgiasChat.setCurrentInput(question);
      }, CONFIRM_SEND_MS);
    }

    button.addEventListener('click', function () {
      if (ready && GorgiasChat.isOpen()) {
        GorgiasChat.close();
      } else if (isPanelOpen()) {
        closePanel(false);
      } else if (ready && unread > 0) {
        openChat(''); // a reply is waiting, go straight to the conversation
      } else {
        openPanel();
      }
    });

    // Lets Google Tag Manager count which topics shoppers tap (event siraat_chat_question, field chat_topic)
    function track(topic) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'siraat_chat_question', chat_topic: topic });
    }

    startButton.addEventListener('click', function () {
      track('other');
      openChat('');
    });

    panel.querySelectorAll('.siraat-chat-panel__chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        track(chip.getAttribute('data-topic') || 'other');
        openChat(chip.getAttribute('data-question') || '');
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isPanelOpen()) closePanel(true);
    });

    document.addEventListener('pointerdown', function (event) {
      if (isPanelOpen() && !wrapper.contains(event.target)) closePanel(false);
    });

    /* ---------- 4. Stay clear of sticky bars and banners in the bottom-right corner ---------- */

    // Checks what sits under the button's resting spot. Anything fixed there (sticky add-to-cart, cookie banner,
    // discount banner) lifts the button and panel above it. Full-screen overlays are ignored.
    var currentLift = 0;

    function fixedAncestor(element) {
      for (var depth = 0; element && element !== document.body && element !== root && depth < 8; depth += 1) {
        // Only fixed elements: sticky columns in the page content (PDP gallery, info) must not count as bars
        if (getComputedStyle(element).position === 'fixed') return element;
        element = element.parentElement;
      }
      return null;
    }

    function measureLift() {
      if (wrapper.getAttribute('data-state') === 'unavailable' || !document.elementsFromPoint) return;

      var rect = button.getBoundingClientRect();
      var restingTop = rect.top + currentLift;
      var restingBottom = rect.bottom + currentLift;
      var points = [
        [rect.left + rect.width / 2, restingTop + rect.height / 2],
        [rect.left + 6, restingBottom - 4]
      ];
      var viewportHeight = window.innerHeight;
      var needed = 0;

      points.forEach(function (point) {
        var stack = document.elementsFromPoint(point[0], point[1]).slice(0, 8);
        stack.forEach(function (element) {
          if (wrapper.contains(element) || element.closest('#gorgias-chat-container')) return;

          var bar = fixedAncestor(element);
          if (!bar) return;

          var barRect = bar.getBoundingClientRect();
          if (barRect.height > viewportHeight * 0.6) return; // overlay or drawer, not a bar
          if (Number(getComputedStyle(bar).opacity) < 0.1) return;

          needed = Math.max(needed, restingBottom - barRect.top + 12);
        });
      });

      needed = Math.round(Math.min(needed, viewportHeight * 0.6));
      if (needed !== currentLift) {
        currentLift = needed;
        wrapper.style.setProperty('--siraat-chat-lift', needed + 'px');
      }
    }

    var settleTimers = [];
    function measureSoon() {
      // Bars fade in and out over about 0.2s, so measure once after scrolling stops and once more after that
      settleTimers.forEach(clearTimeout);
      settleTimers = [setTimeout(measureLift, 150), setTimeout(measureLift, 450)];
    }

    window.addEventListener('scroll', measureSoon, { passive: true });
    window.addEventListener('resize', measureSoon, { passive: true });
    document.addEventListener('click', measureSoon, { passive: true }); // banners closing, variant changes

    onPageLoaded(function () {
      // Cookie and discount banners often appear a few seconds after load
      [500, 2000, 5000].forEach(function (delay) {
        setTimeout(measureLift, delay);
      });
    });
  }
})();
